// features/workstations/components/workstation-topbar.js
// ═══════════════════════════════════════════════════════════
// WORKSTATION TOPBAR — single horizontal bar (matches Domain
// Intelligence visual rhythm).
//
// Layout:
//   [‹]  [● Domain · Strategy ▾]  │  Tab Tab Tab  ·  Tab Tab Tab  ·  Tab
//    ↑        ↑                      ↑
//    back     sibling switcher       9 tabs w/ group separators
//
// The sibling switcher is a dropdown — opens with a click, shows
// all workstations under the same domain + a "+ New strategy"
// footer. Replaces the old 148px side panel.
//
// Tab strip scrolls horizontally on narrow viewports (hidden
// scrollbar) — same pattern as DI's .di-topbar-tabs.
// ═══════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import { WORKSTATION_TABS, tabLabelFor } from '../../../lib/workstations/tab-list';
import { isTabEnabled } from '../../../lib/workstations/config-schema';

// Map domain_key → accent color (matches home.js DOMAIN_ACCENTS)
const DOMAIN_DOT = {
  trading: 'gold',
  deal: 'steel',
  vulture: 'teal',
  moat: 'sage',
  default: 'gold',
};

export default function WorkstationTopbar({
  workstation,
  siblingWorkstations = [],
  activeTab,
  onTabChange,
  onSwitchWorkstation,
  onBackToHome,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const accent = DOMAIN_DOT[workstation?.domain_key] || DOMAIN_DOT.default;
  const hasSiblings = siblingWorkstations.length > 1;

  // Filter + preserve order. Insert a separator flag between groups.
  const visibleTabs = [];
  let lastGroup = null;
  for (const tab of WORKSTATION_TABS) {
    if (!isTabEnabled(workstation?.config, tab.key, workstation)) continue;
    if (lastGroup && tab.group !== lastGroup) {
      visibleTabs.push({ separator: true, key: `sep-${tab.group}` });
    }
    visibleTabs.push(tab);
    lastGroup = tab.group;
  }

  return (
    <div className="ws-topbar">
      {/* Left: back + sibling switcher */}
      <div className="ws-topbar-domain" ref={menuRef}>
        <button
          type="button"
          className="ws-topbar-back"
          onClick={onBackToHome}
          aria-label="Back to Workstations Home"
          title="All workstations"
        >
          ‹
        </button>

        <span className={`ws-topbar-domain-dot ws-dot-${accent}`} />

        <button
          type="button"
          className={`ws-topbar-domain-name${hasSiblings ? ' is-switcher' : ''}`}
          onClick={() => hasSiblings && setMenuOpen((v) => !v)}
          aria-haspopup={hasSiblings ? 'menu' : undefined}
          aria-expanded={hasSiblings ? menuOpen : undefined}
        >
          {workstation?.name || 'Workstation'}
          {hasSiblings && <span className="ws-topbar-chev">▾</span>}
        </button>

        {menuOpen && hasSiblings && (
          <div className="ws-topbar-menu" role="menu">
            <div className="ws-topbar-menu-label">Switch strategy</div>
            {siblingWorkstations.map((w) => (
              <button
                key={w.id}
                type="button"
                className={`ws-topbar-menu-item${w.id === workstation?.id ? ' active' : ''}`}
                onClick={() => {
                  setMenuOpen(false);
                  if (w.id !== workstation?.id) onSwitchWorkstation?.(w);
                }}
              >
                <span className={`ws-topbar-menu-dot ws-dot-${DOMAIN_DOT[w.domain_key] || 'gold'}`} />
                <span className="ws-topbar-menu-name">{w.name}</span>
                {w.id === workstation?.id && <span className="ws-topbar-menu-check">✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Right: horizontal tab strip */}
      <div className="ws-topbar-tabs">
        {visibleTabs.map((t) =>
          t.separator ? (
            <span key={t.key} className="ws-topbar-tab-sep" aria-hidden="true" />
          ) : (
            <button
              key={t.key}
              type="button"
              className={`ws-topbar-tab${activeTab === t.key ? ' active' : ''}`}
              onClick={() => onTabChange?.(t.key)}
            >
              {tabLabelFor(t.key, workstation?.config)}
            </button>
          )
        )}
      </div>
    </div>
  );
}
